import { useEffect, useState } from "react"
import { PropsSingle } from "react-day-picker"
import { UseFormReturn } from "react-hook-form"
import { Calendar } from "@/components/ui/calendar"
import {
  DateInput,
  validDateWhileTypingRegex,
  VISUAL_DATE_FORMAT,
  VISUAL_DATE_SEPARATOR,
} from "@/components/ui/date-picker"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { formatDate } from "@/lib/format-date"
import { NewsForm } from "@/modules/admin/news/types"

type Props = {
  form: UseFormReturn<NewsForm>
  onSelect?: (date: Date | undefined) => void
  defaultValue: Date | undefined
  error?: string
  setError?: (message: string) => void
  label: string
  disabledMatcher?: (date: Date) => boolean
  ref?: React.Ref<HTMLInputElement>
}

export const NewsStartDatePicker = (props: Props) => {
  const { defaultValue, onSelect, error, setError, label, disabledMatcher, form, ref } = props
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(defaultValue)
  const [isFocused, setIsFocused] = useState(false)
  const [open, setOpen] = useState(false)

  const [fieldValue, setFieldValue] = useState<string | undefined>(formatDate(defaultValue, VISUAL_DATE_FORMAT))

  useEffect(() => {
    if (defaultValue) {
      setSelectedDate(defaultValue)
      if (fieldValue !== "") {
        setFieldValue(formatDate(defaultValue, VISUAL_DATE_FORMAT))
      }
    }
  }, [defaultValue, fieldValue])

  const handleSelect: PropsSingle["onSelect"] = (date, selectedDay) => {
    if (formatDate(selectedDate) === formatDate(selectedDay)) return

    setSelectedDate(date)
    onSelect?.(date)
    setFieldValue(formatDate(date, VISUAL_DATE_FORMAT))

    handleOpenChange(false)
    form.trigger("startDate")
  }

  const handleOpenChange = (state: boolean) => {
    setIsFocused(state)
    setOpen(state)
  }

  const keyPressFunc = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      const val = fieldValue
      if (val && (val.length === 3 || val.length === 6)) {
        setFieldValue(val.slice(0, val.length - 1))
      }
    }
  }

  const handleChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    let val = evt.target.value

    if (val && !validDateWhileTypingRegex.test(val)) {
      evt.preventDefault()
      return
    }

    if (val.length === 2 || val.length === 5) {
      val += VISUAL_DATE_SEPARATOR
    }
    form.setValue("startTime", "")
    setFieldValue(val)
    validateDate(val)
  }

  const handleInputFieldClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault()
    setOpen(true)
    setIsFocused(true)
  }

  const handleCalendarIconClick = (e: React.SyntheticEvent<HTMLElement, Event>) => {
    e.preventDefault()
    setOpen((open) => !open)
    setIsFocused(true)
  }

  const validateDate = (input: string) => {
    setIsFocused(input !== "")
    if (input === "") {
      setSelectedDate(undefined)
      onSelect?.(undefined)
      setError?.("common.fieldErrorList.requiredField")
      return
    }
    setError?.("")
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild onClick={handleInputFieldClick}>
        <DateInput
          ref={ref}
          label={label}
          type="text"
          focus={isFocused}
          error={error}
          translateError
          readOnly={false}
          onChange={handleChange}
          onKeyDown={keyPressFunc}
          onCalendarIconClick={handleCalendarIconClick}
          value={fieldValue}
          onBlur={(event) => validateDate(event.target.value)}
        />
      </PopoverTrigger>

      <PopoverContent
        sideOffset={0}
        className="bg-surface w-auto rounded-lg p-0"
        align="start"
        onOpenAutoFocus={(e: Event) => e.preventDefault()}
        onCloseAutoFocus={(e: Event) => e.preventDefault()}>
        <Calendar
          disabledMatcher={disabledMatcher}
          onSelect={handleSelect}
          selected={selectedDate}
          today={selectedDate}
          className="pb-2"
          mode="single"
        />
      </PopoverContent>
    </Popover>
  )
}
